import React, { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  ShieldCheck,
  HeartHandshake,
  Award,
  CheckCircle2,
  MapPin,
} from "lucide-react";
import { BRAND, SERVICES, IMAGES } from "@/lib/site";
import { getCity } from "@/lib/locations";
import ServiceIcon from "@/components/site/ServiceIcon";
import ContactForm from "@/components/site/ContactForm";
import Seo from "@/components/site/Seo";

export default function CityPage() {
  const { citySlug } = useParams();
  const city = getCity(citySlug);

  const path = city ? `/home-care/${city.slug}` : "/";
  const seoTitle = city ? `In-Home Senior Care in ${city.name}, FL` : "";
  const seoDesc = city
    ? `Premium in-home senior care in ${city.name}, Florida. Vetted caregivers, 24/7 availability, personal care, companion care & Alzheimer's care. Serving ${city.neighborhoods}. Call (239) 834-4377 or schedule a free consultation.`
    : "";

  const jsonLd = useMemo(
    () =>
      city
        ? {
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "In-Home Senior Care",
            "provider": {
              "@type": "LocalBusiness",
              "name": BRAND.fullName,
              "telephone": "+1-239-834-4377",
            },
            "areaServed": {
              "@type": "City",
              "name": city.name,
              "containedInPlace": { "@type": "State", "name": "Florida" },
            },
            "url": `https://yourelitehomecare.com${path}`,
            "name": `In-Home Senior Care in ${city.name}, FL`,
            "description": seoDesc,
          }
        : null,
    [city, path, seoDesc]
  );

  if (!city) return <Navigate to="/" replace />;

  return (
    <div data-testid={`page-city-${city.slug}`}>
      <Seo path={path} title={seoTitle} description={seoDesc} jsonLd={jsonLd} />

      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src={IMAGES.hero}
            alt={`Premium in-home senior care in ${city.name}, Florida`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-900/85 to-navy-900/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900/40 via-transparent to-navy-900/60" />
        </div>
        <div className="container-x pt-24 pb-24 md:pt-28 md:pb-32 text-white">
          <Link
            to="/"
            data-testid="city-breadcrumb-home"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-gold-300 hover:text-gold-200"
          >
            <MapPin className="h-4 w-4" strokeWidth={1.75} />
            Southwest Florida • {city.name}
          </Link>
          <h1 className="mt-6 font-serif text-5xl md:text-6xl leading-[1.05] max-w-3xl text-white drop-shadow-[0_2px_12px_rgba(10,25,47,0.6)]">
            {city.heroTagline}
          </h1>
          <p className="mt-6 max-w-2xl text-white/90 text-lg leading-relaxed">
            {city.intro}
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid={`city-${city.slug}-cta-consultation`}
              className="btn-primary"
            >
              Schedule a Free Consultation <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={BRAND.phoneHref}
              data-testid={`city-${city.slug}-cta-call`}
              className="btn-ghost-light"
            >
              <Phone className="h-4 w-4" strokeWidth={1.75} /> {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      {/* LIFESTYLE */}
      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <div>
            <span className="overline">Care that fits {city.name}</span>
            <h2 className="text-4xl md:text-5xl leading-tight">
              Familiar faces.{" "}
              <span className="italic text-gold-500">Uncompromising standards.</span>
            </h2>
            <p className="mt-6 text-navy-700 text-lg leading-relaxed">
              {city.lifestyleParagraph}
            </p>
            <p className="mt-4 text-navy-600 leading-relaxed">
              {city.servicesParagraph}
            </p>
          </div>
          <div>
            <div className="card-soft p-8 md:p-10 bg-navy-50/60">
              <h3 className="font-serif text-2xl text-navy-900">
                Why {city.name} families choose us
              </h3>
              <ul className="mt-5 space-y-4">
                {city.whyLocal.map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <CheckCircle2
                      className="h-5 w-5 text-gold-500 mt-0.5 shrink-0"
                      strokeWidth={1.6}
                    />
                    <span className="text-navy-800">{line}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-8 border-t border-navy-900/10">
                <div className="text-xs uppercase tracking-[0.2em] text-navy-500">
                  Zip codes served
                </div>
                <div className="mt-2 text-navy-800">{city.zips}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section bg-navy-50/50 border-y border-navy-900/5">
        <div className="container-x">
          <div className="max-w-2xl">
            <span className="overline">Every service, delivered locally</span>
            <h2 className="text-4xl md:text-5xl leading-tight">
              Full-service in-home care{" "}
              <span className="italic text-gold-500">in {city.name}.</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`city-${city.slug}-service-${s.slug}`}
                className="card-soft p-8 group"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-navy-900 text-gold-300 group-hover:bg-gold-400 group-hover:text-navy-900 transition-colors">
                  <ServiceIcon name={s.icon} className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-serif text-xl text-navy-900">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-navy-600 leading-relaxed">
                  {s.short}
                </p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-navy-800 group-hover:text-gold-500 transition-colors">
                  Learn more <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="section">
        <div className="container-x grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { I: ShieldCheck, t: "Background-checked caregivers", s: "Every visit, every time." },
            { I: HeartHandshake, t: "Family-first care", s: "We treat your loved ones as our own." },
            { I: Award, t: "Concierge coordination", s: `One dedicated contact for your ${city.name} family.` },
          ].map(({ I, t, s }) => (
            <div key={t} className="flex items-start gap-4">
              <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-navy-50 border border-navy-900/10 text-gold-500">
                <I className="h-5 w-5" strokeWidth={1.6} />
              </span>
              <div>
                <div className="font-serif text-xl text-navy-900">{t}</div>
                <div className="text-navy-600 mt-1">{s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA + FORM */}
      <section className="section bg-navy-50/40 border-t border-navy-900/5">
        <div className="container-x grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <span className="overline">Start the conversation</span>
            <h2 className="text-4xl md:text-5xl leading-tight">
              Speak with a<br />
              <span className="italic text-gold-500">{city.name} care specialist.</span>
            </h2>
            <p className="mt-5 text-navy-600 text-lg leading-relaxed">
              Share a few details and we'll follow up within one business day.
              Prefer to talk now?
            </p>
            <a
              href={BRAND.phoneHref}
              data-testid={`city-${city.slug}-phone-inline`}
              className="inline-flex items-center gap-3 mt-6 text-navy-900 hover:text-gold-500 transition-colors"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-navy-900 text-gold-300">
                <Phone className="h-5 w-5" strokeWidth={1.6} />
              </span>
              <span>
                <span className="block text-sm text-navy-500">Call us</span>
                <span className="font-serif text-2xl">{BRAND.phone}</span>
              </span>
            </a>
            <div className="mt-10 card-soft p-6 bg-white">
              <div className="text-xs uppercase tracking-[0.2em] text-navy-500">
                A common {city.name} question
              </div>
              <div className="mt-2 font-serif text-lg text-navy-900">
                {city.faqExtra.question}
              </div>
              <div className="mt-2 text-navy-600 leading-relaxed">
                {city.faqExtra.answer}
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
